import { EarningsPeriod } from '@api/api/earnings';
import { Entity } from '@api/api/entity-schema';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { RevampTable, TableCell, TableHeader } from '@design/components/table';
import { useCompanyTheme } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { getCalendarQuarterInfo } from '@terminal/views/theme-overview/tab-content/earnings/utils/utils';
import { useThemeEarningsSummaryDrawer } from '@terminal/views/theme-overview/tab-content/theme-earnings/useThemeEarningsSummaryDrawer';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { IndustryThemeRow } from './IndustryThemeRow';
import styles from './IndustryThemesTable.module.scss';
import type { IndustryThemeTimeframe } from './useIndustryThemeMetrics';
import { useIndustryThemesColumns } from './useIndustryThemesColumns';

export const INDUSTRY_THEMES_LIMIT = 5;

export interface IndustryThemesTableProps {
  entity: Entity;
  activeReportingPeriod: EarningsPeriod;
}

export const IndustryThemesTable = ({
  entity,
  activeReportingPeriod,
}: IndustryThemesTableProps) => {
  const { startDate, endDate } = getCalendarQuarterInfo(
    new Date(activeReportingPeriod.reporting_date)
  );
  const timeframe: IndustryThemeTimeframe = { startDate, endDate };
  const { t } = useTranslation(['earnings']);
  const columns = useIndustryThemesColumns();
  const { openDrawer } = useThemeEarningsSummaryDrawer();
  const { data: themes = [], isLoading } = useCompanyTheme({
    entityTag: entity.tag,
    limit: INDUSTRY_THEMES_LIMIT,
    version: 'v6',
  });

  const renderLoadingRows = () =>
    Array.from({ length: INDUSTRY_THEMES_LIMIT }).map((_, index) => (
      <tr key={index}>
        <TableCell
          className={styles.cell}
          colspan={columns.length}
          isNumber={false}
        >
          <SkeletonLoader
            areas={[
              {
                styles: { width: '100%', height: '25px', borderRadius: '8px' },
              },
            ]}
          />
        </TableCell>
      </tr>
    ));

  return (
    <div className={styles.root} data-testid="industry-themes-table">
      <div className={styles.headerSection}>
        <p className={styles.title}>
          {t('earnings:peerComparison.industryThemes.title', {
            name: entity.name_short || entity.name,
            count: INDUSTRY_THEMES_LIMIT,
          })}
        </p>
      </div>
      <div className={styles.tableWrapper}>
        <RevampTable withBodyRowHover withBodyRowCursor>
          <thead>
            <tr>
              {columns.map(column => (
                <TableHeader
                  key={column.id}
                  alignment={column.alignment ?? 'left'}
                  isHoverEnabled={false}
                >
                  {column.header}
                </TableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              renderLoadingRows()
            ) : themes.length === 0 ? (
              <tr>
                <TableCell
                  className={clsx(styles.cell, styles.placeholder)}
                  colspan={columns.length}
                  alignment="start"
                  isNumber={false}
                >
                  {t('earnings:noDataToDisplay')}
                </TableCell>
              </tr>
            ) : (
              themes.map(themeExposure => (
                <IndustryThemeRow
                  key={themeExposure.theme.id}
                  themeExposure={themeExposure}
                  timeframe={timeframe}
                  columns={columns}
                  onClick={() => {
                    openDrawer(
                      themeExposure.theme.id,
                      activeReportingPeriod.reporting_date
                    );
                  }}
                />
              ))
            )}
          </tbody>
        </RevampTable>
      </div>
    </div>
  );
};
